import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { getPaperTypeLabel } from "@/constants/exam";
import type { IExamProgress, ModuleStatus } from "@/apis/exam_progress/types";
import type { ISimpleUser } from "@/apis/exam_papers/getExamReport";
import { useMutationGenerateReport } from "@/apis/exam_progress/generateReport";
import {
  useMutationCreateRetake,
  useMutationDeleteRetake,
} from "@/apis/exam_progress";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/query-keys";
import {
  EllipsisHorizontalIcon,
  DocumentTextIcon,
  PrinterIcon,
} from "@heroicons/react/20/solid";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/dropdown";

import { message } from "antd";

import { ModuleTimer } from "./ModuleTimer";
import { useMutationGenerateReports } from "@/apis/exam_assignments/generateReports";
import type { IGenerateReportsResponse } from "@/apis/exam_assignments/generateReports";
import { Switch } from "@/components/switch";
import { PlusIcon } from "@heroicons/react/20/solid";
import {
  isSubjectAvailableInExamSet,
  getAvailableSubjectsFromExamSet,
} from "@/apis/exam_assignments/utils";
import type { SubjectType } from "@/apis/exam_progress/types";
import type { IExamSet } from "@/apis/exam_sets/types";

interface ProgressItem {
  progress: IExamProgress;
  user: ISimpleUser;
  progress_rate: number;
}

interface ExamAssignmentMonitorTableProps {
  progressList?: ProgressItem[];
  examSet?: IExamSet | null;
  onSubjectToggle?: (
    progressId: string,
    subject: SubjectType,
    enabled: boolean
  ) => void;
  onRemoveStudent?: (studentId: string) => void;
  onToggleStudentManageModal?: () => void;
  isUpdatingSubject?: boolean;
  isUpdating?: boolean;
}

const getStatusBadge = (status: ModuleStatus) => {
  const statusConfig = {
    completed: {
      color: "green" as const,
      label: "Completed",
    },
    in_progress: {
      color: "amber" as const,
      label: "In Progress",
    },
    not_started: {
      color: "zinc" as const,
      label: "Not Started",
    },
  };

  const config = statusConfig[status] || statusConfig.not_started;
  return <Badge color={config.color}>{config.label}</Badge>;
};

// Helper function to get subject display name
const getSubjectDisplayName = (subject: SubjectType): string => {
  const subjectNames: Record<SubjectType, string> = {
    reading_writing: "R&W",
    math: "Math",
    english: "English",
    reading: "Reading",
    science: "Science",
  };
  return subjectNames[subject] || subject;
};

// Helper function to get subject badge color
const getSubjectBadgeColor = (subject: SubjectType) => {
  return "zinc" as const;
};

export const ExamAssignmentMonitorTable: React.FC<
  ExamAssignmentMonitorTableProps
> = ({
  progressList = [],
  examSet,
  onSubjectToggle,
  onRemoveStudent,
  onToggleStudentManageModal,
  isUpdatingSubject = false,
  isUpdating = false,
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { mutate: generateReport, isPending: isGenerating } =
    useMutationGenerateReport();
  const { mutate: generateBulkReports, isPending: isBulkGenerating } =
    useMutationGenerateReports();
  const { mutate: createRetake, isPending: isCreatingRetake } =
    useMutationCreateRetake();
  const { mutate: deleteRetake, isPending: isDeletingRetake } =
    useMutationDeleteRetake();

  // Get available subjects from exam set
  const availableSubjects = getAvailableSubjectsFromExamSet(examSet);

  // Score formatting functions
  const formatScore = (score?: number | null) => {
    if (score === undefined || score === null) return "-";
    return score.toString();
  };

  const formatSubjectScore = (score?: number | null) => {
    if (score === undefined || score === null) return "0";
    return score.toString();
  };

  const getSubjectScore = (subjects: any[], subjectType: string) => {
    const subject = subjects?.find((s: any) => s.subject === subjectType);
    return subject?.total_score;
  };

  const formatTotalScoreWithDetails = (item: ProgressItem) => {
    // Check if exam report exists
    if (!item.progress.exam_report) {
      return <span className="text-gray-500">-</span>;
    }

    const report = item.progress.exam_report;
    const totalScore = formatScore(report.total_score);

    if (!report.subjects || report.subjects.length === 0) {
      return <div className="font-medium text-gray-900">{totalScore}</div>;
    }

    // Show different score breakdown based on exam type
    if (examSet?.exam_type === "SAT") {
      const rwScore = getSubjectScore(report.subjects, "reading_writing");
      const mathScore = getSubjectScore(report.subjects, "math");

      return (
        <div>
          <div className="font-medium text-gray-900">{totalScore}</div>
          <div className="text-xs text-gray-500 mt-1">
            R&W: {formatSubjectScore(rwScore)}, Math:{" "}
            {formatSubjectScore(mathScore)}
          </div>
        </div>
      );
    } else if (examSet?.exam_type === "ACT") {
      // For ACT, show composite score and individual subject scores
      return (
        <div>
          <div className="font-medium text-gray-900">{totalScore}</div>
          <div className="text-xs text-gray-500 mt-1">
            {availableSubjects.map((subject, index) => {
              const score = getSubjectScore(report.subjects, subject);
              return (
                <span key={subject}>
                  {index > 0 && ", "}
                  {getSubjectDisplayName(subject)}: {formatSubjectScore(score)}
                </span>
              );
            })}
          </div>
        </div>
      );
    }

    return <div className="font-medium text-gray-900">{totalScore}</div>;
  };

  const handleGenerateReport = (progressId: string) => {
    generateReport(progressId, {
      onSuccess: () => {
        if (id) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.examAssignment.monitor(id),
          });
          message.success("Report generated successfully");
        }
      },
    });
  };

  const handleBulkGenerateReports = () => {
    if (!id) return;

    generateBulkReports(id, {
      onSuccess: (response) => {
        if (response.data) {
          message.success(
            `Bulk report generation completed! Generated ${response.data.generated_reports} reports.`
          );
          queryClient.invalidateQueries({
            queryKey: QueryKeys.examAssignment.monitor(id),
          });
        }
      },
      onError: (error: any) => {
        message.error(error?.message || "Failed to generate bulk reports");
      },
    });
  };

  // 과목별 paper 상태 확인 헬퍼 함수
  const getSubjectPaperStatus = (item: ProgressItem, subject: SubjectType) => {
    const paper = item.progress.assigned_papers?.find(
      (paper) => paper.subject === subject
    );
    return paper?.status || "not_started";
  };

  const handleCreateRetake = (progressId: string) => {
    if (isCreatingRetake) return;

    const confirmMessage = `Are you sure you want to create a retake for this student?

This will:
• Create a new exam attempt
• Allow the student to retake the entire exam
• Keep the original attempt for comparison`;

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    createRetake(progressId, {
      onSuccess: () => {
        if (id) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.examAssignment.monitor(id),
          });
          message.success("Retake created successfully");
        }
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to create retake"
        );
      },
    });
  };

  const handleDeleteRetake = (originalProgressId: string) => {
    if (isDeletingRetake) return;

    const confirmMessage = `⚠️ WARNING: PERMANENT DELETION ⚠️

Are you absolutely sure you want to delete this retake attempt?

This action will:
• Permanently delete all retake progress data
• Remove all retake answers and scores
• Cannot be undone or recovered
• The original attempt will remain intact`;

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    deleteRetake(originalProgressId, {
      onSuccess: () => {
        if (id) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.examAssignment.monitor(id),
          });
          message.success("Retake deleted successfully");
        }
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to delete retake"
        );
      },
    });
  };

  const hasRetake = (userId: string) => {
    return progressList.some(
      (item: ProgressItem) => item.progress.user_id === userId && item.progress.is_retake
    );
  };

  const getOriginalProgress = (userId: string) => {
    return progressList.find(
      (item: ProgressItem) => item.progress.user_id === userId && !item.progress.is_retake
    );
  };

  return (
    <div className="p-6 bg-white rounded-md border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">Students Management</h2>
        <div className="flex items-center gap-2">
          <Button onClick={onToggleStudentManageModal} className="text-sm">
            <PlusIcon className="h-4 w-4 mr-1" />
            Manage Students
          </Button>
          <Dropdown>
            <DropdownButton outline>
              <EllipsisHorizontalIcon className="h-5 w-5 text-gray-500" />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem
                onClick={handleBulkGenerateReports}
                disabled={isBulkGenerating || progressList.length === 0}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                {isBulkGenerating ? "Generating..." : "Generate Reports"}
              </DropdownItem>
              <DropdownItem
                onClick={() => navigate(`/exam_assignments/${id}/print-score-reports`)}
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print Score Reports
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <Table dense>
        <TableHead>
          <TableRow>
            <TableHeader>Student</TableHeader>
            <TableHeader className="hidden lg:table-cell">Subjects</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader className="hidden 2xl:table-cell">
              Current Module
            </TableHeader>
            <TableHeader>Score</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {progressList.length > 0 ? (
            progressList.map((item) => {
              // 현재 진행 중인 모듈 찾기
              const currentModule = item.progress.assigned_papers
                ?.find((paper) => paper.status === "in_progress")
                ?.module_progress?.find(
                  (module) => module.status === "in_progress"
                );

              return (
                <TableRow key={item.progress.id}>
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {item.user.name}
                        </span>
                        {item.progress.is_retake && (
                          <Badge color="amber" className="text-xs">
                            Retake
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.user.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="space-y-1">
                      {availableSubjects.map((subject) => (
                        <div key={subject} className="flex items-center gap-2">
                          <Badge color={getSubjectBadgeColor(subject)} className="text-xs">
                            {getSubjectDisplayName(subject)}
                          </Badge>
                          <Switch
                            checked={
                              !!item.progress.assigned_papers?.some(
                                (paper) => paper.subject === subject
                              )
                            }
                            onChange={(checked) =>
                              onSubjectToggle?.(
                                item.progress.id,
                                subject,
                                checked
                              )
                            }
                            disabled={
                              isUpdatingSubject ||
                              !isSubjectAvailableInExamSet(subject, examSet) ||
                              getSubjectPaperStatus(item, subject) === "in_progress" ||
                              getSubjectPaperStatus(item, subject) === "completed"
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {getStatusBadge(item.progress.status)}
                      {item.progress.status === "in_progress" && (
                        <div className="mt-2 w-fit">
                          <div className="relative w-20 bg-gray-100 rounded-full h-4 flex items-center">
                            <div
                              className="bg-blue-600 h-4 rounded-full"
                              style={{ width: `${item.progress_rate}%` }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-[10px] font-medium text-gray-900">
                                {Math.floor(item.progress_rate)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden 2xl:table-cell">
                    {currentModule ? (
                      <div className="text-xs">
                        <span className="font-medium">
                          {currentModule.subject &&
                            getPaperTypeLabel(currentModule.subject)}
                        </span>
                        {", "}
                        Module {currentModule.module_no}
                        <div className="text-gray-500 text-xs">
                          {currentModule.completed_questions || 0} /{" "}
                          {currentModule.total_questions || 0} questions
                          {currentModule?.started_at && (
                            <span className="inline">
                              {" "}
                              (
                              <span className="inline-block">
                                <ModuleTimer currentModule={currentModule} />
                              </span>
                              )
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </TableCell>
                  <TableCell>{formatTotalScoreWithDetails(item)}</TableCell>
                  <TableCell>
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.progress.exam_report ? (
                        <Button
                          outline
                          onClick={() =>
                            navigate(
                              `/exam_progress/${item.progress.id}/report`
                            )
                          }
                          className="text-sm"
                        >
                          Report
                        </Button>
                      ) : (
                        item.progress.status === "completed" && (
                          <Button
                            onClick={() =>
                              handleGenerateReport(item.progress.id)
                            }
                            className="text-sm bg-amber-600 hover:bg-amber-700"
                            disabled={isGenerating}
                          >
                            {isGenerating ? "Generating..." : "Generate Report"}
                          </Button>
                        )
                      )}
                      <Dropdown>
                        <DropdownButton outline>
                          <EllipsisHorizontalIcon className="h-5 w-5 text-gray-500" />
                        </DropdownButton>
                        <DropdownMenu>
                          <DropdownItem
                            onClick={() =>
                              navigate(
                                `/exam_progress/${item.progress.id}/edit_answers`
                              )
                            }
                          >
                            Edit Answers
                          </DropdownItem>
                          {item.progress.exam_report && (
                            <DropdownItem
                              onClick={() =>
                                handleGenerateReport(item.progress.id)
                              }
                              disabled={isGenerating}
                            >
                              Regenerate Reports
                            </DropdownItem>
                          )}
                          {/* Retake인 경우 메뉴 */}
                          {item.progress.is_retake && (
                            <DropdownItem
                              onClick={() =>
                                handleDeleteRetake(
                                  item.progress.original_attempt_id ||
                                    item.progress.id
                                )
                              }
                              disabled={isDeletingRetake}
                              danger
                            >
                              Delete Retake
                            </DropdownItem>
                          )}
                          {/* Retake가 아닌 경우 메뉴 */}
                          {!item.progress.is_retake && (
                            <>
                              {item.progress.status === "completed" &&
                                !hasRetake(item.progress.user_id) && (
                                  <DropdownItem
                                    onClick={() =>
                                      handleCreateRetake(item.progress.id)
                                    }
                                    disabled={isCreatingRetake}
                                  >
                                    Create Retake
                                  </DropdownItem>
                                )}
                              <DropdownItem
                                onClick={() =>
                                  onRemoveStudent?.(item.progress.user_id)
                                }
                                disabled={isUpdating}
                                danger
                              >
                                Remove Student
                              </DropdownItem>
                            </>
                          )}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-4">
                No students assigned.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
